import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

interface LoginRequest {
  userNameOrEmail: string;
  password: string;
}

interface VerificationRequest {
  email: string;
  password: string;
  code: string;
}

interface RegisterRequest {
  email: string;
  password: string;
  userName?: string;
  fullName?: string;
}

interface AuthResponse {
  userId: string;
  userName: string;
  email: string;
  accessToken: string;
  expiresAtUtc: string;
}
interface PasswordResetRequest {
  email: string;
  password: string;
  verificationCode?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Adjust base URL if your API is hosted under a different path
  private baseUrl = `${environment.azure.apiBaseUrl}/v1/Auth`;

  constructor(private http: HttpClient, @Inject(PLATFORM_ID) private platformId: Object) { }

  login(payload: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/login`, payload);
  }

  verifyEmail(payload: VerificationRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/verify-email`, payload);
  }

  register(payload: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/register`, payload);
  }

  resetPassword(email: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/reset-password`, { email });
  }

  updatePassword(payload: PasswordResetRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/update-password`, payload);
  }

  // Helpers the app might find useful
  logout(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('access_token');
    }
  }

  getToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('access_token');
    }
    return null;
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp;
      const now = Math.floor(Date.now() / 1000);
      return now > expiry;
    } catch (e) {
      console.error('Error parsing token:', e);
      return true;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    //return token !== null;
    return token !== null && !this.isTokenExpired(token); // Enable this line if you want to check for token expiry
  }

  getCurrentUser(): AuthResponse | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    const token = this.getToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        userId: payload.sub || payload.userId || '',
        userName: payload.userName || payload.name || '',
        email: payload.email || '',
        accessToken: token,
        expiresAtUtc: new Date((payload.exp || 0) * 1000).toISOString()
      };
    } catch {
      return null;
    }
  }
}
